import ReactDOM from "react-dom";
import React, {ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {computePosition, offset, flip, shift, autoUpdate, type Placement, type VirtualElement} from '@floating-ui/dom';

import {useAppState, useRootStore} from "@stores/AppStateContext";
import {HEADER_MENU_HEIGHT} from "@stores/constants";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";

import {MenuAlignment, MenuPlacement} from "../../../utils/normalizePosition";
import {Pointer, PointerPosition} from "../../components/Pointer/Pointer";
import {editorContextRootRef} from '../EditorReact';
import {leftMenuContextRootRef} from '../ListView';

import './ctx-menu-position.css';

export interface ContextMenuParams {
    [x: string]: any;
}

export interface ContextMenuAdditionalParamsType {
    [x: string]: any;
}

export type MousePositionParams = {
    clientX: number,
    clientY: number,
} | {
    getTargetBoundingBox: () => DOMRect;
}

export interface ContextMenuProps {
    type: ContextMenuType,
    id: string,
    clientX?: number,
    clientY?: number,
    isDark?: boolean,
    params?: ContextMenuParams,
    onClose?: () => void,
    menuPlacement?: MenuPlacement;
    menuAlignment?: MenuAlignment;
    withPointer?: boolean;
    isPointerLarge?: boolean;
    getTargetBoundingBox?: () => DOMRect | undefined;
    hotKeys?: Record<string, () => void>;
    VERTICAL_SHIFT?: number,
    HORIZONTAL_SHIFT?: number,
    minWidth?: number,
    maxWidth?: number,
    withOverlay?: boolean,
    shouldForcePlace?: boolean,
}

export type ContextMenuType =
    'item'
    | 'task'
    | 'avatar'
    | 'document'
    | 'workspace'
    | 'workspace-row'
    | 'add-to-cluster'
    | 'editor-text'
    | 'editor-trigger'
    | 'editor-table'
    | 'editor-link'
    | 'editor-image'
    | 'editor-plus'
    | 'editor-embed'
    | 'editor-callout'
    | 'notification-centre'
    | 'select-options'
    | 'tree-select-dropdown'
    | 'calendar-select-options'
    | 'calendar-select-repeat-options'
    | 'quiz'
    | 'quiz-question'
    | 'slide'
    | 'acknowledgments-overview'
    | 'avatar-group-users'
    | 'access-role-members'
    | 'access-role-locations'
    | 'access-role-row-options'
    | 'audit-row-options'
    | 'issue-row-options'
    | 'taskList'
    | 'taskListUnit'
    | 'taskListUnit-select-options'
    | 'taskList-schedule'
    | 'taskListRow-create'
    | 'tree-quick-actions'
    | 'tree-header-options'
    | 'section-select-options'
    | 'tree-view-options'
    | 'profile'
    | 'library-row-options'
    | 'ws-member-row-options'

const getElement = (id: string) => window.document.getElementById(id);

const POINTER_LONG = 10;
const POINTER_SHORT = 4;
const POINTER_LONG_LARGE = 24;
const POINTER_SHORT_LARGE = 10;

const editorContextsTriggeredWithoutRightClickEvent = new Set([
    'editor-text',
    'editor-trigger',
    'editor-link',
]);

const editorContexts = new Set([
    ...editorContextsTriggeredWithoutRightClickEvent,
    'editor-table',
    'editor-plus',
    'editor-image',
    'editor-embed',
    'editor-callout',
]);

export const getRenderingScope = (type: ContextMenuType, params: any): {
    anchorScope: HTMLElement | null;
    popupScope: HTMLElement | null;
    hideWhenBehindHeader?: boolean;
} => {
    const normal = getElement('contextMenu');
    const low = getElement('contextMenuLow');

    switch (type) {
        case 'editor-plus':
        case 'editor-text':
        case 'editor-trigger':
        case 'editor-link':
        case 'editor-table':
        case 'editor-image':
        case 'add-to-cluster':
        case 'document':
            return {
                anchorScope: editorContextRootRef.current,
                popupScope: normal,
                hideWhenBehindHeader: true,
            };
        case 'item':
            if (params.inEditorView === true) {
                return {
                    anchorScope: editorContextRootRef.current,
                    popupScope: normal,
                    hideWhenBehindHeader: true,
                };
            }
            return {
                anchorScope: leftMenuContextRootRef.current,
                popupScope: normal,
            };
        case 'taskList-schedule':
            return {
                anchorScope: low || normal,
                popupScope: low || normal,
            };
        case 'quiz':
        case 'quiz-question':
        default:
            return {
                anchorScope: normal,
                popupScope: normal,
            };
    }
}

const mapPlacementToFloating = (placement: MenuPlacement = 'block-end', alignment: MenuAlignment = 'start'): Placement => {
    const base = (() => {
        switch (placement) {
            case 'block-start': return 'top';
            case 'block-end': return 'bottom';
            case 'inline-start': return 'left';
            case 'inline-end': return 'right';
            default: return 'bottom';
        }
    })();

    if (alignment === 'center') {
        return base as Placement;
    }
    return `${base}-${alignment}` as Placement;
};

const mapFloatingToMenuPlacement = (floatingPlacement: Placement): MenuPlacement => {
    const base = floatingPlacement.split('-')[0];
    switch (base) {
        case 'top': return 'block-start';
        case 'bottom': return 'block-end';
        case 'left': return 'inline-start';
        case 'right': return 'inline-end';
        default: return 'block-end';
    }
};

const createVirtualElementFromRect = (rect: DOMRect): VirtualElement => ({
    getBoundingClientRect: () => rect,
});

const createRectFromCoords = (x: number, y: number): DOMRect =>
    new DOMRect(x, y, 0, 0);

const resolveShifts = (requestedPlacement: MenuPlacement | undefined, HORIZONTAL_SHIFT?: number, VERTICAL_SHIFT?: number) => {
    const base = requestedPlacement ?? 'block-end';
    let h = HORIZONTAL_SHIFT;
    let v = VERTICAL_SHIFT;

    if (h === undefined) {
        h = (base === 'inline-end' || base === 'inline-start') ? 0 : 16;
    }
    if (v === undefined) {
        v = (base === 'block-end' || base === 'block-start') ? 0 : 16;
    }

    return {h, v};
};

const computePointerPosition = (
    targetRect: DOMRect,
    menuPlacement: MenuPlacement,
    HORIZONTAL_SHIFT: number,
    VERTICAL_SHIFT: number,
    isPointerLarge: boolean,
): PointerPosition => {
    const PLong = isPointerLarge ? POINTER_LONG_LARGE : POINTER_LONG;
    const PShort = isPointerLarge ? POINTER_SHORT_LARGE : POINTER_SHORT;
    const {x: Tx, y: Ty, width: Tw, height: Th} = targetRect;

    switch (menuPlacement) {
        case "block-end":
            return {
                left: (2 * Tx + Tw) / 2 - PLong / 2,
                top: Ty + Th + VERTICAL_SHIFT,
                direction: 'top'
            }
        case "block-start":
            return {
                left: (2 * Tx + Tw) / 2 - PLong / 2,
                top: Ty - PShort - VERTICAL_SHIFT,
                direction: 'bottom'
            }
        case "inline-end":
            return {
                left: Tx + Tw + HORIZONTAL_SHIFT,
                top: (2 * Ty + Th) / 2 - PLong / 2,
                direction: 'left'
            }
        case "inline-start":
        default:
            return {
                left: Tx - PShort - HORIZONTAL_SHIFT,
                top: (2 * Ty + Th) / 2 - PLong / 2,
                direction: 'right'
            }
    }
};

/* if context menu opens automatically on click in editor we need a catch-up to avid closing it on the same click handle */
export const isTriggeredBySelectionChangeInEditor = (contextMenu: ContextMenuProps | null) =>
    contextMenu !== null
    && editorContextsTriggeredWithoutRightClickEvent.has(contextMenu.type);
export const isEditorContext = (contextMenu: ContextMenuProps | null) => contextMenu !== null && editorContexts.has(contextMenu.type);

export interface MenuPosition {
    params: any,
    actualDirection: MenuPlacement,
    style: {
        top?: string;
        left?: string;
    },
    pointer?: PointerPosition;
    hiddenByHeader?: boolean;
}

type RenderFunction = (type: ContextMenuType, params: any, handleClose: any, isDark?: boolean) => ReactElement | null;

export interface ContextMenuManagerProps {
    renderContextMenu: RenderFunction;
    renderScope?: HTMLElement;
}

const ContextMenuManagerBase = ({renderContextMenu, renderScope}: ContextMenuManagerProps) => {
    const appState = useAppState();
    const isClickInsideRef = useRef<boolean>(false);

    const isInContextMenuClick = (clickedTarget: HTMLElement) => {
        return !!(
            clickedTarget.closest('.ctx-menu-position')
            || (isTriggeredBySelectionChangeInEditor(appState.contextMenu) && clickedTarget.closest('.ER-wrapper'))
        )
    }

    const handleClose = useCallback(() => {
        if (appState.contextMenu === null) {
            return;
        }
        appState.setContextMenu(null);
    }, []);

    const onDocumentMouseDown = useCallback((event: MouseEvent) => {
        const clickedTarget: HTMLElement = event.target as HTMLElement;
        isClickInsideRef.current = isInContextMenuClick(clickedTarget);
    }, [isClickInsideRef]);

    const onDocumentClick = useCallback((event: MouseEvent): void => {
        if (event.defaultPrevented) {
            return;
        }
        const clickedTarget: HTMLElement = event.target as HTMLElement;

        if (isClickInsideRef.current || isInContextMenuClick(clickedTarget)) {
            return;
        }

        handleClose();
    }, [handleClose, isClickInsideRef]);

    useEffect(() => {
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('mousedown', onDocumentMouseDown);
        }
    }, [onDocumentClick, onDocumentMouseDown]);

    const stateCtxMenu = appState.contextMenu;
    const menus: ContextMenuProps[] = [];
    if (stateCtxMenu) menus.push(stateCtxMenu);
    menus.push(...appState.additionalContextMenus);

    return (
        <>
            {menus.map((menu, idx) => {
                const contentFor = (m: ContextMenuProps) =>
                    renderContextMenu(
                        m.type,
                        m.params,
                        m === stateCtxMenu ? handleClose : appState.removeAdditionalContextMenu,
                        m.isDark
                    );

                const content = contentFor(menu);
                if (!content) {
                    return null;
                }

                const scopes = renderScope
                    ? {anchorScope: renderScope, popupScope: renderScope}
                    : getRenderingScope(menu.type, menu.params);

                if (!scopes.anchorScope || !scopes.popupScope) {
                    return null;
                }

                return (
                    <CtxMenu
                        key={menu.id}
                        anchorScope={scopes.anchorScope}
                        popupScope={scopes.popupScope}
                        ctxMenu={menu}
                        content={content}
                        layerIndex={idx}
                        hideWhenBehindHeader={scopes.hideWhenBehindHeader}
                    />
                );
            })}
        </>
    )
}

export const ContextMenuManager = observer(ContextMenuManagerBase);

export interface CtxMenuProps {
    ctxMenu: ContextMenuProps;
    content: ReactElement;
    anchorScope: HTMLElement;
    popupScope: HTMLElement;
    layerIndex?: number;
    hideWhenBehindHeader?: boolean;
}

const Z_STEP = 50;
const Z_MENU_BASE = 4500;
const Z_MIDDLE_BASE = 950;

export const CtxMenu = (
    {
        ctxMenu,
        content,
        anchorScope,
        popupScope,
        layerIndex = 0,
        hideWhenBehindHeader = false,
    }: CtxMenuProps
) => {
    const appState = useAppState();
    const rootStore = useRootStore();

    const [menuPosition, setMenuPosition] = useState<MenuPosition | undefined>(undefined);
    const {
        type,
        params,
        isDark = false,
        isPointerLarge = false,
        minWidth,
        maxWidth,
        shouldForcePlace = false,
        withOverlay = false,
    } = ctxMenu || {};

    const isMiddleLayer = popupScope.id === 'contextMenuMiddle';
    const baseZ = isMiddleLayer ? Z_MIDDLE_BASE : Z_MENU_BASE;
    const zMenu = baseZ + layerIndex * Z_STEP;
    const zOverlay = zMenu - 10;

    const prevWidthRef = useRef<number>(0);
    const [recalculationCount, forceUpdate] = useState<number>(0);
    const popupRef = useRef<HTMLDivElement | null>(null);

    const resizeObserverCallback: ResizeObserverCallback = (entries) => {
        const size = Array.isArray(entries[0].contentBoxSize)
            ? entries[0].contentBoxSize[0].inlineSize
            : (entries[0].contentBoxSize as any)?.inlineSize ?? entries[0].contentRect.width;

        if (size !== prevWidthRef.current) {
            prevWidthRef.current = size;
            forceUpdate(count => count + 1);
        }
    };

    useEffect(() => {
        if (!ctxMenu || !popupRef.current) {
            return;
        }
        const ro = new ResizeObserver(resizeObserverCallback);
        ro.observe(popupRef.current);
        return () => ro.disconnect();
    }, [ctxMenu, type, params]);

    useLayoutEffect(() => {
        const {
            clientX, clientY, withPointer,
            menuPlacement, menuAlignment, getTargetBoundingBox,
            VERTICAL_SHIFT, HORIZONTAL_SHIFT,
        } = ctxMenu || {};
        const isMobile = screenSizeProvider.view !== 'desktop';
        if (isMobile && !shouldForcePlace) {
            return;
        }
        if (!content) {
            return;
        }

        const floatingEl = popupRef.current;
        const targetRect = getTargetBoundingBox
            ? getTargetBoundingBox()
            : (clientX !== undefined && clientY !== undefined ? createRectFromCoords(clientX, clientY) : undefined);

        if (!floatingEl || !targetRect || !anchorScope) {
            return;
        }

        const reference = createVirtualElementFromRect(targetRect);
        const resolvedPlacement = mapPlacementToFloating(menuPlacement, menuAlignment);
        const {h: resolvedHShift, v: resolvedVShift} = resolveShifts(menuPlacement, HORIZONTAL_SHIFT, VERTICAL_SHIFT);

        let cancelled = false;

        const updatePosition = async () => {
            const currentTargetRect = getTargetBoundingBox ? getTargetBoundingBox() : targetRect;
            if (!floatingEl || !currentTargetRect) {
                return;
            }

            const hiddenByHeader = hideWhenBehindHeader && currentTargetRect.top < HEADER_MENU_HEIGHT;

            const reference = createVirtualElementFromRect(currentTargetRect);
            const {x, y, placement} = await computePosition(reference, floatingEl, {
                strategy: 'fixed',
                placement: resolvedPlacement,
                middleware: [
                    offset(({placement: currentPlacement}) => {
                        const base = currentPlacement.split('-')[0];
                        const mainAxis = (base === 'left' || base === 'right') ? resolvedHShift : resolvedVShift;
                        return {mainAxis};
                    }),
                    flip(),
                    shift({padding: 8}),
                ],
            });

            if (cancelled) {
                return;
            }

            const actualDirection = mapFloatingToMenuPlacement(placement);
            const pointer = withPointer
                ? computePointerPosition(currentTargetRect, actualDirection, resolvedHShift, resolvedVShift, !!isPointerLarge)
                : undefined;

            setMenuPosition({
                params: params!,
                actualDirection,
                style: {
                    top: `${y}px`,
                    left: `${x}px`,
                },
                pointer,
                hiddenByHeader,
            });
        };

        const cleanup = autoUpdate(anchorScope, floatingEl, updatePosition, {
            ancestorScroll: true,
            ancestorResize: true,
            elementResize: true,
        });

        void updatePosition();

        return () => {
            cancelled = true;
            cleanup();
        };
    }, [
        anchorScope,
        recalculationCount,
        menuPosition?.params,
        params,
        popupRef,
        ctxMenu,
        ctxMenu?.type,
        ctxMenu?.getTargetBoundingBox,
        rootStore.leftMenuWidth,
        shouldForcePlace,
        content,
    ]);

    const isMobile = screenSizeProvider.view !== 'desktop';
    const isVisible = isMobile && !shouldForcePlace ? true : !!menuPosition && !menuPosition.hiddenByHeader;

    return ReactDOM.createPortal(
        <>
            {(withOverlay || (isMobile && !shouldForcePlace)) && (
                <div
                    key={`${ctxMenu.type}#${ctxMenu.id}overlay`}
                    className="ctx-menu-overlay"
                    style={{zIndex: zOverlay}}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (appState.latestAdditionalCtxMenu?.id === ctxMenu.id) {
                            appState.removeAdditionalContextMenu();
                        } else {
                            appState.setContextMenu(null);
                        }
                    }}
                />
            )}
            <div
                className={classNames({
                    'ctx-menu-position': true,
                    "ctx-menu-position-mobile": shouldForcePlace
                })}
                key={`${ctxMenu.type}#${ctxMenu.id}`}
                ref={popupRef}
                style={{
                    ...menuPosition?.style,
                    zIndex: zMenu,
                    minWidth: isMobile && !shouldForcePlace ? undefined : minWidth,
                    maxWidth: isMobile && !shouldForcePlace ? undefined : maxWidth,
                    position: 'fixed',
                    visibility: isVisible ? 'visible' : 'hidden',
                }}
                onClick={() => {
                    if (appState.latestAdditionalCtxMenu && appState.latestAdditionalCtxMenu.id !== ctxMenu.id) {
                        appState.removeAdditionalContextMenu();
                    }
                }}
            >
                {content}
            </div>
            {menuPosition?.pointer && !menuPosition.hiddenByHeader ? (
                <Pointer isDark={isDark} isLarge={isPointerLarge} {...menuPosition.pointer}/>
            ) : null}
        </>,
        popupScope
    );
}
